import React, { useEffect } from 'react';
import { css } from 'emotion';
import {
  ButtonPrimary,
  TextLink,
  LoadingIndicator,
  ButtonSecondary,
} from '@spotify-internal/encore-web';
import { Modal } from '@spotify-internal/anchor-ds';
import { paths } from 'client/shared/Paths';
import { useT } from 'client/i18n/hooks/useT';
import { events } from './events';

function MTSpotifyDistributionModal({
  onClickClose,
  onOptInClick,
  podcastImageFull,
  isLoading,
  isError,
}: {
  onClickClose: () => void;
  onOptInClick: () => void;
  podcastImageFull?: string;
  isLoading: boolean;
  isError: boolean;
}) {
  const t = useT();
  // analytics
  useEffect(() => {
    events.distributeToSpotifyMTModalScreenViewed();
  }, []);

  return (
    <Modal
      isShowing
      isShowingCloseButton
      aria-labelledby="distribute-top-spotify-title"
      onClose={() => {
        events.distributeToSpotifyMTExitButtonClicked();
        onClickClose();
      }}
    >
      <Modal.Title id="distribute-top-spotify-title">
        {t(
          'PreDistributionModalScreen.BinaryDistributionPrompt.MTSpotifyDistributionModal.Title',
          'Distribute to Spotify',
          'Distribute to Spotify modal title'
        )}
      </Modal.Title>
      <Modal.Subtitle>
        {t(
          'PreDistributionModalScreen.BinaryDistributionPrompt.MTSpotifyDistributionModal.Message',
          "To submit an episode that includes Spotify songs, you'll need to distribute your show to Spotify.",
          'Distribute to Spotify modal message'
        )}
      </Modal.Subtitle>
      {podcastImageFull ? (
        <Modal.Image
          src={podcastImageFull}
          alt={t(
            'PreDistributionModalScreen.BinaryDistributionPrompt.MTSpotifyDistributionModal.PodcastImgAlt',
            'Podcast cover art',
            'Podcast cover art image screenreader information'
          )}
          className={css`
            max-width: 200px;
            max-height: 200px;
            border-radius: 8px;
          `}
        />
      ) : (
        <div
          className={css`
            margin: 20px 0;
          `}
        >
          <LoadingIndicator indicatorSize={LoadingIndicator.sm} />
        </div>
      )}
      {isError && (
        <Modal.Error>
          {t(
            'PreDistributionModalScreen.BinaryDistributionPrompt.ErrorMessage',
            'Hm, something went wrong. Please try again or if the issue persists, reach out to us at {helpPageLink}.',
            'Distribute to Spotify modal error message',
            {
              helpPageLink: (
                <TextLink
                  key="help-link"
                  href={paths.getHelpPage('home')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {paths.getHelpPage('home')}
                </TextLink>
              ),
            }
          )}
        </Modal.Error>
      )}
      <Modal.Footer>
        <ButtonSecondary
          as="a"
          onClick={() => {
            events.distributeToSpotifyMTLearnMoreButtonClicked();
          }}
          href="https://anch.co/3iOP2uG"
          target="_blank"
        >
          {t('Button.LearnMore', 'Learn more', 'Learn more button text')}
        </ButtonSecondary>
        <ButtonPrimary
          onClick={() => {
            events.distributeToSpotifyMTDistributeButtonClicked();
            onOptInClick();
          }}
          disabled={isLoading}
        >
          {isError
            ? t('Button.Retry', 'Retry', 'Retry button text')
            : t(
                'ButtonPublishToSpotify',
                'Publish to Spotify',
                'Publish to Spotify button text'
              )}
        </ButtonPrimary>
      </Modal.Footer>
    </Modal>
  );
}

export { MTSpotifyDistributionModal };
